import { CARRYFORWARD_FLOOR_ISO } from '@utazzo/shared';
import { Activity, Briefcase, Percent, Trophy } from 'lucide-react';
import React from 'react';

import { ReportKpiCard, ReportKpiPairCard } from '../report-cards';
import { LegacyTable, ReportSection, type LegacyCell } from '../ReportSection';

import {
  filterEntriesByRowData,
  funnelRatios,
  largeCombined,
  num,
  ratioCell,
  ratioHint,
  splitCell,
  splitDrillCell,
  subCurrent,
  subMtd,
  subReached,
  withPct,
} from './band-helpers';

import type { BandProps } from './band-props';
import type { AgentTrackerRow, LeadListQuery } from '@utazzo/shared';

/**
 * The Lead status band, split so its blocks land in the client dashboard flow
 * (pure relocation - no data/logic change). `variant="status"` (position 6) =
 * the Lead Status KPIs + Lead Status by Agent table; `variant="largeDestination"`
 * (position 5, inside the Active Lead Breakup section) = the Large Lead
 * Destination pivot. Both variants read the SAME props.
 */
export const LeadStatusBand = ({
  variant,
  report,
  totals,
  scopedRow,
  scopedWon,
  scopedActive,
  loading,
  dash,
  agentEntries,
  agentCell,
  expandAllAction,
  drillCell,
  destinationPivotData,
}: BandProps & { variant: 'status' | 'largeDestination' }): React.JSX.Element => {
  if (variant === 'largeDestination') {
    // Drop agents with no large leads anywhere (noise), keeping a reporting
    // manager whose team has some so the hierarchy view stays intact.
    const hasLargeDest = (r: AgentTrackerRow): boolean =>
      r.largeDestinations.some((d) => d.cur + d.cf > 0);
    const largeEntries = filterEntriesByRowData(agentEntries, report, hasLargeDest);
    // Destination columns = union of every destination present for any agent,
    // ordered by total (cur + cf) desc then name; "Unspecified" always last.
    const destTotals = new Map<string, number>();
    for (const d of totals?.largeDestinations ?? []) {
      destTotals.set(d.destination, (destTotals.get(d.destination) ?? 0) + d.cur + d.cf);
    }
    for (const { row } of agentEntries) {
      for (const d of row.largeDestinations) {
        if (!destTotals.has(d.destination)) destTotals.set(d.destination, d.cur + d.cf);
      }
    }
    const destinations = [...destTotals.keys()].sort((a, b) => {
      if (a === 'Unspecified') return 1;
      if (b === 'Unspecified') return -1;
      return (destTotals.get(b) ?? 0) - (destTotals.get(a) ?? 0) || a.localeCompare(b);
    });
    // Destination NAME -> id, read off the destination pivot (same labels; null
    // id = the "Unspecified" bucket). A large-lead destination whose name isn't
    // in the pivot (e.g. a carry-forward-only destination with no leads in the
    // period) can't be resolved to an id, so that cell stays a plain number.
    const destIdByName = new Map<string, string | null>();
    (destinationPivotData?.columns ?? []).forEach((name, i) => {
      destIdByName.set(name, destinationPivotData?.columnIds[i] ?? null);
    });
    // Per-destination drill: large leads only, OPEN, for that destination.
    const destCellQuery = (name: string): Partial<LeadListQuery> | null => {
      if (!destIdByName.has(name)) return null;
      const destId = destIdByName.get(name) ?? null;
      return {
        largeLeadQuery: true,
        currentBucket: 'OPEN',
        ...(destId ? { destinationId: destId } : { destinationUnspecified: true }),
      };
    };
    const destRow = (r: AgentTrackerRow): LegacyCell[] => {
      const id = (r.agentId as string | null) ?? null;
      const byName = new Map(r.largeDestinations.map((d) => [d.destination, d]));
      const cells = destinations.map((name) => {
        const curr = byName.get(name)?.cur ?? 0;
        const cf = byName.get(name)?.cf ?? 0;
        const q = destCellQuery(name);
        return q
          ? splitDrillCell(drillCell, curr, cf, id, `${r.agentName} - ${name} (large)`, q)
          : splitCell(curr, cf);
      });
      const cur = r.largeDestinations.reduce((sum, d) => sum + d.cur, 0);
      const cf = r.largeDestinations.reduce((sum, d) => sum + d.cf, 0);
      return [
        ...cells,
        splitDrillCell(drillCell, cur, cf, id, `${r.agentName} - Large total`, {
          largeLeadQuery: true,
          currentBucket: 'OPEN',
        }),
      ];
    };
    return (
      <ReportSection title="Large Lead Destination" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headers={['Agent', ...destinations, 'Total']}
          rows={largeEntries.map(({ row, ...entry }) => [
            { node: agentCell({ row, ...entry }), sort: row.agentName },
            ...destRow(row),
          ])}
          totals={totals && report ? ['Total', ...destRow(totals)] : undefined}
        />
      </ReportSection>
    );
  }
  const subName = (code: string): string =>
    report?.subStatuses.find((sub) => sub.code === code)?.name ?? code;
  // The active status master row id, for the "Total Active" drill.
  const activeStatusId = report?.statuses.find((s) => s.code === 'active')?.id;
  // Lead-status numbers drill to leads created since the carry-forward floor
  // (i.e. the combined "current month + carry forward" pool) that currently
  // match the filter. MTD cells (leads that ENTERED a stage this month) aren't
  // reproducible with list filters, so they render as plain non-clickable
  // numbers; funnel ratios are % and never drill.
  const statusDrillCell = (
    value: number,
    agentId: string | null,
    title: string,
    q: Partial<LeadListQuery>,
    denom: number | null = null,
  ): LegacyCell =>
    drillCell(withPct(value, denom), value, value, title, {
      ...(agentId ? { assignedAgentId: agentId } : {}),
      createdAtFrom: CARRYFORWARD_FLOOR_ISO,
      ...q,
    });
  const statusRow = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    const subs = report?.subStatuses ?? [];
    const f = funnelRatios(r);
    // Each sub-status "current" cell's % is of the agent's Total Active now, so
    // the row shows how the active pool splits across Proposal / Warm / Hot.
    const totalActive = subs.reduce((sum, sub) => sum + subCurrent(r, sub.code), 0);
    return [
      ...subs.flatMap((sub): LegacyCell[] => [
        statusDrillCell(
          subCurrent(r, sub.code),
          id,
          `${sub.name} (current)`,
          {
            currentSubStatusId: sub.id,
            currentBucket: 'OPEN',
          },
          totalActive,
        ),
        subMtd(r, sub.code),
        subReached(r, sub.code),
      ]),
      statusDrillCell(
        totalActive,
        id,
        'Total active',
        activeStatusId ? { currentStatusId: activeStatusId, currentBucket: 'OPEN' } : {},
      ),
      ratioCell(f.propToWarm),
      ratioCell(f.warmToHot),
      ratioCell(f.hotToWon),
      ratioCell(f.propToLoss),
      ratioCell(f.warmToLoss),
      ratioCell(f.hotToLoss),
      statusDrillCell(largeCombined(r), id, 'Large leads', {
        largeLeadQuery: true,
        currentBucket: 'OPEN',
      }),
    ];
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-14">
        <ReportKpiCard
          icon={Activity}
          tone="primary"
          className="col-span-2"
          value={
            scopedRow
              ? num(
                  (report?.subStatuses ?? []).reduce(
                    (s, sub) => s + subCurrent(scopedRow, sub.code),
                    0,
                  ),
                )
              : dash
          }
          label="Total Active Leads"
          hint={
            scopedRow
              ? `${num(scopedActive ?? 0)} current + ${num(
                  (report?.subStatuses ?? []).reduce(
                    (s, sub) => s + subCurrent(scopedRow, sub.code),
                    0,
                  ) - (scopedActive ?? 0),
                )} carry fwd`
              : undefined
          }
        />
        <ReportKpiCard
          icon={Trophy}
          tone="success"
          className="col-span-2"
          value={scopedRow ? num((scopedWon ?? 0) + scopedRow.carriedIn.currentWon) : dash}
          label="Converted (Won)"
          hint={
            scopedRow
              ? `${num(scopedWon ?? 0)} current + ${num(scopedRow.carriedIn.currentWon)} carry fwd`
              : undefined
          }
        />
        <ReportKpiCard
          icon={Briefcase}
          tone="warning"
          className="col-span-2"
          value={scopedRow ? num(largeCombined(scopedRow)) : dash}
          label="Large Leads"
        />
        <ReportKpiPairCard
          icon={Percent}
          label="Funnel Conversion"
          className="col-span-4"
          parts={[
            {
              label: `${subName('proposal')} > ${subName('warm')}`,
              value: scopedRow ? funnelRatios(scopedRow).propToWarm.pct : dash,
              hint: scopedRow ? ratioHint(funnelRatios(scopedRow).propToWarm) : undefined,
            },
            {
              label: `${subName('warm')} > ${subName('hot')}`,
              value: scopedRow ? funnelRatios(scopedRow).warmToHot.pct : dash,
              hint: scopedRow ? ratioHint(funnelRatios(scopedRow).warmToHot) : undefined,
            },
            {
              label: `${subName('hot')} > Won`,
              value: scopedRow ? funnelRatios(scopedRow).hotToWon.pct : dash,
              hint: scopedRow ? ratioHint(funnelRatios(scopedRow).hotToWon) : undefined,
            },
          ]}
        />
        <ReportKpiPairCard
          icon={Percent}
          tone="danger"
          label="Funnel Drop-off (to Loss)"
          className="col-span-4"
          parts={[
            {
              label: `${subName('proposal')} > Loss`,
              value: scopedRow ? funnelRatios(scopedRow).propToLoss.pct : dash,
              hint: scopedRow ? ratioHint(funnelRatios(scopedRow).propToLoss) : undefined,
            },
            {
              label: `${subName('warm')} > Loss`,
              value: scopedRow ? funnelRatios(scopedRow).warmToLoss.pct : dash,
              hint: scopedRow ? ratioHint(funnelRatios(scopedRow).warmToLoss) : undefined,
            },
            {
              label: `${subName('hot')} > Loss`,
              value: scopedRow ? funnelRatios(scopedRow).hotToLoss.pct : dash,
              hint: scopedRow ? ratioHint(funnelRatios(scopedRow).hotToLoss) : undefined,
            },
          ]}
        />
      </div>

      <ReportSection title="Lead Status by Agent" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headerGroups={[
            { label: '', colSpan: 1 },
            {
              label: 'Active Leads (Current + MTD + Cumulative)',
              colSpan: (report?.subStatuses.length ?? 0) * 3 + 1,
            },
            { label: 'Funnel Ratios', colSpan: 6 },
            { label: '', colSpan: 1 },
          ]}
          headers={[
            'Agent',
            ...(report?.subStatuses.flatMap((sub) => [
              `${sub.name} (Cur)`,
              `${sub.name} (MTD)`,
              `${sub.name} (Cumulative)`,
            ]) ?? []),
            'Total Active',
            `${subName('proposal')} > ${subName('warm')}`,
            `${subName('warm')} > ${subName('hot')}`,
            `${subName('hot')} > Won`,
            `${subName('proposal')} > Loss`,
            `${subName('warm')} > Loss`,
            `${subName('hot')} > Loss`,
            'Large Leads',
          ]}
          rows={agentEntries.map(({ row, ...entry }) => [
            { node: agentCell({ row, ...entry }), sort: row.agentName },
            ...statusRow(row),
          ])}
          totals={totals && report ? ['Total', ...statusRow(totals)] : undefined}
        />
      </ReportSection>
    </div>
  );
};
